/**
 * useFrontDeskArrivals.ts
 *
 * Watches this computer's front desk for patients tapping their phone on the
 * pad, and offers the tapped patient's chart here.
 *
 * Mounted once from `Layout`, so it runs on every page: the point of the
 * feature is that whatever the front-desk staffer happens to be looking at,
 * they are told who just walked in and can open that chart in one click.
 *
 * It announces rather than navigates. Six editors in this app protect unsaved
 * work only against the browser closing, which does not cover an in-app
 * navigation, and there is no navigation guard anywhere — so jumping the page
 * on its own would silently discard a half-booked appointment or an unsent
 * campaign. The click is the consent.
 *
 * Shape follows `useNotificationPopups`: a short interval that skips itself
 * while the tab is hidden, a `navigateRef` kept fresh so the interval never
 * closes over a stale navigate, and silent catches on the network. The
 * differences are deliberate:
 *
 *   - 3 s rather than 5 s, because a patient is standing at the desk.
 *   - It also ticks the moment the tab becomes visible or regains focus. A
 *     staffer who clicks back into the browser should not wait out an
 *     interval while the patient waits with them.
 *   - Claiming is a write, and exactly one browser tab may win it. The seen
 *     set (persisted per desk) plus the cross-tab channel keep the losing
 *     attempts down; the server's conditional update is what actually
 *     guarantees one winner. A lost race is therefore completely silent —
 *     surfacing it would put an error on screen for every arrival at a desk
 *     with two tabs open.
 *
 * Everything decidable without React lives in `lib/frontDeskReader.ts`.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/store/toastStore';
import { pollArrivals, claimArrival, type FrontDeskArrival } from '@/services/frontDesk.service';
import {
  loadConfig,
  loadSeen,
  saveSeen,
  cursorFrom,
  openChannel,
  tabId,
  CONFIG_EVENT,
  type FrontDeskConfig,
} from '@/lib/frontDeskReader';

const POLL_INTERVAL_MS = 3000;

/**
 * How long an arrival stays on screen waiting to be opened. Long enough that a
 * staffer who looked away still sees it, short enough that a busy desk does not
 * accumulate a wall of them. The arrival itself expires server-side well after
 * this, so a missed toast costs a patient search, not the check-in.
 */
const ARRIVAL_TOAST_MS = 30000;

function sameConfig(a: FrontDeskConfig | null, b: FrontDeskConfig | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.enabled === b.enabled &&
    a.accountID === b.accountID &&
    a.userID === b.userID &&
    a.deskID === b.deskID &&
    a.deskName === b.deskName
  );
}

/**
 * "Jane D." from the two pieces the server sends. Staff recognise a patient
 * from this; the waiting room learns nothing more than a first name.
 */
function displayName(arrival: FrontDeskArrival): string {
  const first = (arrival.patientFirstName ?? '').trim();
  const initial = (arrival.patientLastInitial ?? '').trim().charAt(0);
  if (first && initial) return `${first} ${initial}.`;
  if (first) return first;
  if (initial) return `${initial}.`;
  return '';
}

export function useFrontDeskArrivals(): void {
  const { isAuthenticated, accountId, user } = useAuth();
  const sessionAccountId = accountId ?? 0;
  const sessionUserId = user?.id ?? 0;

  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const [config, setConfig] = useState<FrontDeskConfig | null>(null);

  // Re-read the per-computer setting whenever it could have changed: another
  // tab writing localStorage fires `storage`, and this tab's own Settings save
  // fires CONFIG_EVENT (the `storage` event never fires in the writing tab).
  useEffect(() => {
    const reread = () => {
      const next = loadConfig(sessionAccountId, sessionUserId);
      setConfig((prev) => (sameConfig(prev, next) ? prev : next));
    };
    reread();
    window.addEventListener('storage', reread);
    window.addEventListener(CONFIG_EVENT, reread);
    return () => {
      window.removeEventListener('storage', reread);
      window.removeEventListener(CONFIG_EVENT, reread);
    };
  }, [sessionAccountId, sessionUserId]);

  const deskID = config?.deskID ?? 0;
  const active = isAuthenticated && config !== null && deskID > 0;

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    const seen = loadSeen(deskID);
    const channel = openChannel();

    // A sibling tab took this one. Recording it here means this tab never even
    // attempts the claim, rather than attempting it and losing.
    const unsubscribe = channel.onClaimed((arrivalID) => {
      if (seen.has(arrivalID)) return;
      seen.set(arrivalID, Date.now());
      saveSeen(deskID, seen);
    });

    const handle = async (arrival: FrontDeskArrival) => {
      const claim = await claimArrival(arrival.arrivalID, tabId());
      if (cancelled) return;

      channel.postClaimed(arrival.arrivalID);

      const name = displayName(arrival);

      // The arrival announces itself; it does not move the page. Opening the
      // chart automatically would discard whatever the staffer was in the
      // middle of — a half-booked appointment, an unsent campaign, an
      // in-progress import. Those editors guard themselves against closing the
      // browser, but nothing in this app guards an in-app navigation, so the
      // draft would go with no warning and no undo. One click is cheaper than
      // that, and the toast is a real button.
      toast.custom({
        variant: 'success',
        title: claim.checkedIn
          ? (name ? `${name} has arrived` : 'A patient has arrived')
          : (name ? `${name} is here` : 'A patient is here'),
        description: claim.checkedIn
          ? "Today's visit is checked in. Select to open the chart."
          : 'No visit is booked today. Select to open the chart.',
        duration: ARRIVAL_TOAST_MS,
        onClick: () => navigateRef.current(claim.redirect),
      });
    };

    const tick = async () => {
      if (cancelled) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }
      try {
        const arrivals = await pollArrivals(deskID, cursorFrom(seen));
        for (const arrival of arrivals) {
          if (cancelled) return;
          if (seen.has(arrival.arrivalID)) continue;

          // Marked seen *before* the claim: a claim this tab has already sent
          // must not be sent again by the next tick, whatever its outcome.
          seen.set(arrival.arrivalID, Date.now());
          saveSeen(deskID, seen);

          try {
            await handle(arrival);
          } catch {
            // Another tab won the race, or the arrival aged out. Both are the
            // ordinary case and neither is the staffer's problem.
          }
        }
      } catch {
        // Network blip — silently skip this tick.
      }
    };

    const run = () => {
      void tick();
    };

    run();
    const intervalId = window.setInterval(run, POLL_INTERVAL_MS);

    // Coming back to the tab should show the patient standing at the desk now,
    // not on the next interval boundary.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', run);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', run);
      unsubscribe();
      channel.close();
    };
  }, [active, deskID]);
}
